import {
  Webhook as WebhookDto,
  WebhookDelivery as WebhookDeliveryDto,
  WebhookDeliveryStatusEnum,
  WebhookProviderDescriptor as WebhookProviderDescriptorDto,
  WebhookProviderEnum,
  WebhookStatusEnum,
} from '@electricjs/core_entity-client';

import { simpleOrganization } from './organizationData';

// How many webhooks the organization is allowed to have (`limit` in the list response).
export const WEBHOOK_LIMIT = 20;

export const buildMockWebhooks = (): WebhookDto[] => [
  {
    id: 'webhook-generic-1',
    organization_id: simpleOrganization.id,
    name: 'Employee sync',
    url: 'https://hooks.simple-org.com/electric/employees',
    topics: ['employee.lifecycle'],
    status: WebhookStatusEnum.Active,
    provider: WebhookProviderEnum.Generic,
    config: {},
    has_credentials: false,
    created_at: '2026-05-12T00:00:00Z',
  },
  {
    id: 'webhook-jsm-1',
    organization_id: simpleOrganization.id,
    name: 'Jira Service Management',
    url: 'https://simple-org.atlassian.net/webhooks/incidents',
    topics: ['employee.lifecycle', 'employee.groups'],
    status: WebhookStatusEnum.Inactive,
    provider: WebhookProviderEnum.Jsm,
    config: { automation_rule_id: 'rule-123' },
    has_credentials: true,
    created_at: '2026-06-01T00:00:00Z',
  },
];

export const mockWebhookProviders: WebhookProviderDescriptorDto[] = [
  {
    id: WebhookProviderEnum.Generic,
    kind: 'webhook',
    label: 'Generic webhook',
    detect_hosts: [],
    config_fields: [],
  },
  {
    id: WebhookProviderEnum.Jsm,
    kind: 'webhook',
    label: 'Jira Service Management',
    detect_hosts: ['atlassian.net'],
    url_hint: 'https://your-site.atlassian.net/...',
    config_fields: [
      {
        key: 'automation_rule_id',
        type: 'text',
        required: true,
        secret: false,
        label_key: 'webhooks.providers.jsm.automationRuleId',
      },
      {
        key: 'token',
        type: 'password',
        required: true,
        secret: true,
        label_key: 'webhooks.providers.jsm.token',
      },
    ],
  },
  {
    id: WebhookProviderEnum.Slack,
    kind: 'chat',
    label: 'Slack',
    detect_hosts: ['hooks.slack.com'],
    url_hint: 'https://hooks.slack.com/services/...',
    config_fields: [
      {
        key: 'channel',
        type: 'text',
        required: false,
        secret: false,
        label_key: 'webhooks.providers.slack.channel',
      },
      {
        key: 'username',
        type: 'text',
        required: false,
        secret: false,
        label_key: 'webhooks.providers.slack.username',
      },
    ],
  },
  {
    id: WebhookProviderEnum.Zendesk,
    kind: 'webhook',
    label: 'Zendesk',
    detect_hosts: [],
    detect_host_suffixes: ['.zendesk.com'],
    url_hint: 'https://your-subdomain.zendesk.com',
    config_fields: [
      {
        key: 'client_id',
        type: 'text',
        required: true,
        secret: false,
        label_key: 'webhooks.providers.zendesk.clientId',
        helper_key: 'webhooks.providers.zendesk.clientIdHelp',
      },
      {
        key: 'client_secret',
        type: 'password',
        required: true,
        secret: true,
        label_key: 'webhooks.providers.zendesk.clientSecret',
        helper_key: 'webhooks.providers.zendesk.clientSecretHelp',
      },
      {
        key: 'requester_email',
        type: 'text',
        required: true,
        secret: false,
        label_key: 'webhooks.providers.zendesk.requesterEmail',
        helper_key: 'webhooks.providers.zendesk.requesterEmailHelp',
      },
      {
        key: 'priority',
        type: 'select',
        required: false,
        secret: false,
        label_key: 'webhooks.providers.zendesk.priority',
        options: ['low', 'normal', 'high', 'urgent'],
      },
    ],
  },
];

export const buildMockWebhookDeliveries = (
  webhookId: string
): WebhookDeliveryDto[] => [
  {
    id: `${webhookId}-delivery-1`,
    webhook_id: webhookId,
    event_type: 'employee.created',
    topic: 'employee.lifecycle',
    is_test: false,
    status: WebhookDeliveryStatusEnum.Success,
    response_status: 200,
    latency_ms: 142,
    created_at: '2026-07-01T10:00:00Z',
  },
  {
    id: `${webhookId}-delivery-2`,
    webhook_id: webhookId,
    event_type: 'employee.offboarded',
    topic: 'employee.lifecycle',
    is_test: false,
    status: WebhookDeliveryStatusEnum.Failed,
    response_status: 500,
    latency_ms: 980,
    created_at: '2026-07-02T10:00:00Z',
  },
  {
    id: `${webhookId}-delivery-3`,
    webhook_id: webhookId,
    event_type: 'employee.updated',
    topic: 'employee.lifecycle',
    is_test: true,
    status: WebhookDeliveryStatusEnum.Success,
    response_status: 200,
    latency_ms: 120,
    created_at: '2026-07-03T10:00:00Z',
  },
];
